import { Download, Search } from "@mui/icons-material";
import {
  Alert,
  Button,
  Chip,
  IconButton,
  Input,
  Link,
  Option,
  Select,
  Sheet,
  Stack,
  Table,
  Tooltip,
  Typography,
} from "@mui/joy";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { HTTPError } from "griptonite-api-client/client";
import { ApiCreditRedemptionAdmin, CreditRedemptionStatus } from "griptonite-api-client/types";
import { useMemo, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import api from "@/core/api";
import {
  useCreditSystemProductsQuery,
  useCreditSystemRedemptionsQuery,
  useVenueCreditSystemQuery,
} from "@/hooks/credits";
import { useCurrentVenueQuery } from "@/hooks/venues";
import downloadBlob from "@/utils/downloadBlob";

import Avatar from "../Avatar";
import { SUPPORT_EMAIL } from "../ManageCreditSystem/ManageCreditSystem";

const STATUS_LABELS: Record<CreditRedemptionStatus, string> = {
  PENDING: "Pending",
  FULFILLED: "Fulfilled",
  VOIDED: "Voided",
};

const STATUS_COLORS = {
  PENDING: "warning",
  FULFILLED: "success",
  VOIDED: "neutral",
} as const;

// In pixels. The user column has no width of its own: it takes up the rest of the table, down to
// `USER_MIN_WIDTH`. The gym column only shows when the log covers every gym.
const USER_MIN_WIDTH = 300;
const COLUMN_WIDTHS = {
  time: 170,
  gym: 150,
  reward: 150,
  cost: 80,
  transactionId: 140,
  status: 80,
};

// `productNames` is undefined until the rewards load, so a row doesn't flash the placeholder.
// Clicking a row opens the member's account with the redemption highlighted in their log.
const RedemptionsTable = ({
  redemptions,
  productNames,
  showGym,
  venueUuid,
}: {
  redemptions: ApiCreditRedemptionAdmin[];
  productNames?: Map<string, string>;
  showGym: boolean;
  venueUuid: string;
}) => {
  const navigate = useNavigate();
  const minTableWidth =
    USER_MIN_WIDTH +
    Object.entries(COLUMN_WIDTHS).reduce(
      (total, [column, width]) => total + (column === "gym" && !showGym ? 0 : width),
      0,
    );
  return (
    <Sheet variant="outlined" sx={{ borderRadius: "md", overflow: "auto" }}>
      <Table
        stickyHeader
        hoverRow
        sx={{
          "& tbody tr": { cursor: "pointer" },
          "--TableRow-hoverBackground": (theme) => theme.vars.palette.neutral["100"],
          // Fixed widths, so the columns stay put as rows load and the window resizes.
          tableLayout: "fixed",
          width: "100%",
          minWidth: minTableWidth,
          "--TableCell-paddingX": "12px",
          "--TableCell-paddingY": "8px",
          "thead th": { verticalAlign: "middle", backgroundColor: "neutral.100" },
          "tbody td": { verticalAlign: "middle", overflow: "hidden", textOverflow: "ellipsis" },
        }}
      >
        <colgroup>
          <col style={{ width: COLUMN_WIDTHS.time }} />
          <col />
          {showGym ? <col style={{ width: COLUMN_WIDTHS.gym }} /> : null}
          <col style={{ width: COLUMN_WIDTHS.reward }} />
          <col style={{ width: COLUMN_WIDTHS.cost }} />
          <col style={{ width: COLUMN_WIDTHS.transactionId }} />
          <col style={{ width: COLUMN_WIDTHS.status }} />
        </colgroup>
        <thead>
          <tr>
            <th>Time</th>
            <th>User</th>
            {showGym ? <th>Gym</th> : null}
            <th>Reward</th>
            <th>Cost</th>
            <th>Transaction ID</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {redemptions.map((redemption) => (
            <tr
              key={redemption.uuid}
              onClick={() =>
                navigate({
                  to: "/gym/$venueUuid/loyalty/accounts/$accountUuid",
                  params: { venueUuid, accountUuid: redemption.account },
                  search: { redemption: redemption.uuid },
                })
              }
            >
              <td>
                <Typography noWrap level="inherit">
                  {format(new Date(redemption.createdAt), "d MMM yyyy, HH:mm")}
                </Typography>
              </td>
              <td>
                <Stack direction="row" alignItems="center" gap={1.5}>
                  <Avatar user={redemption.user} size={32} />
                  <Stack>
                    <Typography fontWeight="bold" noWrap>
                      {redemption.user.name}
                    </Typography>
                    <Typography level="body-sm" textColor="neutral.600" noWrap>
                      {redemption.user.email}
                    </Typography>
                  </Stack>
                </Stack>
              </td>
              {showGym ? <td>{redemption.venue.name}</td> : null}
              <td>
                <Typography fontWeight="bold" noWrap>
                  {productNames ? (productNames.get(redemption.product) ?? "Unknown reward") : null}
                </Typography>
              </td>
              <td>{redemption.cost}</td>
              <td>
                <Typography fontFamily="monospace">{redemption.transactionId}</Typography>
              </td>
              <td>
                <Chip size="sm" variant="soft" color={STATUS_COLORS[redemption.status]}>
                  {STATUS_LABELS[redemption.status]}
                </Chip>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Sheet>
  );
};

// The rewards claimed at this gym, newest first. With `allGyms`, it covers every gym the viewer
// can see: all of the system's for an admin, and just their own for staff.
export default function RedemptionsLog({ allGyms }: { allGyms: boolean }) {
  const { data: venue } = useCurrentVenueQuery();
  const systemQuery = useVenueCreditSystemQuery(venue?.uuid);
  const system = systemQuery.data;
  const productsQuery = useCreditSystemProductsQuery(system?.slug, venue?.uuid);
  const [productUuid, setProductUuid] = useState<string | null>(null);

  // Redemptions only carry the reward's uuid, so a rename shows up in the log as well.
  const productNames = useMemo(
    () => productsQuery.data && new Map(productsQuery.data.map((p) => [p.uuid, p.name])),
    [productsQuery.data],
  );

  // The input updates as the user types; the search waits for a pause.
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const updateSearchQuery = useDebouncedCallback(setSearchQuery, 300);

  const search = searchQuery.trim();
  const filters = useMemo(
    () => ({
      ...(allGyms ? {} : { venue: venue?.uuid }),
      ...(productUuid ? { product: productUuid } : {}),
      ...(search ? { search } : {}),
    }),
    [allGyms, venue?.uuid, productUuid, search],
  );
  const redemptionsQuery = useCreditSystemRedemptionsQuery(
    venue ? system?.slug : undefined,
    filters,
  );
  const redemptions = redemptionsQuery.data?.pages.flatMap((page) => page.data);
  const hasFilters = !!productUuid || !!search;

  // The file has the same filters as the table.
  const exportMutation = useMutation({
    mutationFn: async (slug: string) => {
      const blob = await api.credits.getCreditSystemRedemptionsCsv(slug, filters);
      downloadBlob(blob, `redemptions-${slug}-${format(new Date(), "yyyy-MM-dd")}.csv`);
    },
    onError: () => alert("Failed to export the redemptions. Please try again"),
  });

  let content;
  if (systemQuery.error instanceof HTTPError && systemQuery.error.response.status === 404) {
    content = (
      <Alert color="neutral">
        <Typography>
          Credits aren’t set up for this gym yet. To get started, contact{" "}
          <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>.
        </Typography>
      </Alert>
    );
  } else if (systemQuery.error || redemptionsQuery.error) {
    content = <Alert color="danger">Couldn’t load the redemptions.</Alert>;
  } else if (redemptions && venue) {
    if (!redemptions.length) {
      content = (
        <Typography textColor="neutral.500">
          {hasFilters ? "No redemptions match these filters." : "No redemptions yet."}
        </Typography>
      );
    } else {
      content = (
        <>
          <RedemptionsTable
            redemptions={redemptions}
            productNames={productNames}
            showGym={allGyms}
            venueUuid={venue.uuid}
          />
          {redemptionsQuery.hasNextPage ? (
            <Button
              variant="outlined"
              color="neutral"
              loading={redemptionsQuery.isFetchingNextPage}
              onClick={() => redemptionsQuery.fetchNextPage()}
              sx={{ alignSelf: "center" }}
            >
              Load more
            </Button>
          ) : null}
        </>
      );
    }
  }

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        flexWrap="wrap"
      >
        <Input
          startDecorator={<Search />}
          placeholder="Name, email or transaction ID"
          value={searchInput}
          onChange={(event) => {
            setSearchInput(event.target.value);
            updateSearchQuery(event.target.value);
          }}
          sx={{ flex: 1, minWidth: 240, maxWidth: 400 }}
        />
        <Stack direction="row" alignItems="center" gap={1}>
          <Select
            placeholder="All rewards"
            value={productUuid}
            onChange={(_, value) => setProductUuid(value)}
            slotProps={{ button: { sx: { minWidth: 160 } } }}
          >
            <Option value={null}>All rewards</Option>
            {productsQuery.data?.map((product) => (
              <Option key={product.uuid} value={product.uuid}>
                {product.name}
              </Option>
            ))}
          </Select>
          <Tooltip title="Download redemptions as CSV">
            <IconButton
              onClick={() => system && venue && exportMutation.mutate(system.slug)}
              disabled={!system || !venue || exportMutation.isPending}
              loading={exportMutation.isPending}
            >
              <Download />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      {content}
    </>
  );
}
